import { useEffect, useState } from 'react';

const C = { t1: '#1A1C2E', t3: '#9295A5', border: '#E4E0D8' };

const Raw = ({ html, style }: { html: string; style?: React.CSSProperties }) => (
  <div style={style} dangerouslySetInnerHTML={{ __html: html }} />
);

/** Force any inline SVG height up to a large fixed size for the expanded modal. */
function enlargeSvgs(html: string) {
  return html.replace(/(<svg\b[^>]*?style="[^"]*?)height:[^;"]+/g, '$1height:320px');
}

/** The corner-arrows "expand" button used on chart cards. */
export function ExpandButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      title="Expand"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 24,
        height: 24,
        borderRadius: 4,
        cursor: 'pointer',
        color: C.t3,
        border: '1px solid transparent',
        background: 'transparent',
        flexShrink: 0,
      }}
    >
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
        <path
          d="M1 5V1H5M8 1H12V5M12 8V12H8M5 12H1V8"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

type ChartModalState = { title: string; sub: string; html: string };

function ChartModal({ title, sub, html, onClose }: ChartModalState & { onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 24px 64px rgba(0,0,0,.3)',
          width: 'min(960px,92vw)',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 18px',
            borderBottom: `1px solid ${C.border}`,
            flexShrink: 0,
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.t1 }}>{title}</div>
            <div style={{ fontSize: 10, color: C.t3, marginTop: 2 }}>{sub}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: 6,
              cursor: 'pointer',
              color: C.t3,
              border: `1px solid ${C.border}`,
              background: '#fff',
              fontSize: 16,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
        <Raw
          style={{ padding: 24, overflow: 'auto' }}
          html={`<div style="width:100%;min-height:340px;display:flex;align-items:center;justify-content:center"><div style="width:100%">${enlargeSvgs(html)}</div></div>`}
        />
      </div>
    </div>
  );
}

/** Hook that owns the modal state. Call `open(title, sub, html)` from an ExpandButton; render `node`. */
export function useChartExpand() {
  const [modal, setModal] = useState<ChartModalState | null>(null);
  const open = (title: string, sub: string, html: string) => setModal({ title, sub, html });
  const node = modal ? <ChartModal {...modal} onClose={() => setModal(null)} /> : null;
  return { open, node };
}
